/**
 * PRD.329 — Laboratory Requisition write path.
 *
 * `Requisition → Lab Order + Lab Tests + dedicated Invoice + invoice lines`
 * is written as ONE local transaction (`db.writeTransaction`). The PRD.329
 * §9.3 spike showed the writes inside a PowerSync write transaction are
 * captured under one CRUD `tx_id`, handed to the connector as one
 * CrudTransaction (one syncWrite POST), and syncWrite applies one POST inside
 * one Postgres BEGIN/COMMIT. Locally a throw rolls everything back, including
 * the CRUD queue.
 *
 * RUNTIME GATE: the browser → PowerSync → syncWrite → PostgreSQL leg is a
 * mandatory pre-merge check (PRD.329 report §9.4.4). Until it passes, end-to-end
 * atomicity is established by the offline spike + code reading, NOT observed.
 *
 * HARD RULE (spike finding S4b): inside the transaction callback every
 * statement goes through the supplied `tx` executor. Calling `db.execute` /
 * `db.getAll` there deadlocks on the write lock. `writeLabRequisitionPlan`
 * therefore receives ONLY the executor — it has no way to reach `db` — and the
 * plan is built as pure data before the transaction starts.
 *
 * Layer 1 (kept alongside): every id is derived from the attempt's
 * `requisitionId`, so retrying the SAME attempt converges on the rows already
 * written. It does not deduplicate independently created requisitions.
 *
 * Payment is deliberately outside this transaction (PRD.329 §12): Pay Now hands
 * the returned invoice to the existing billing UI.
 */
import {
  buildLabRequisitionPlan,
  type LabRequisitionPlan,
  type LabRequisitionPlanInput,
} from '@helios/data-access';
import {
  invoiceSummary,
  type InvoiceItem,
  type DiscountType,
} from '../pages/patient/consultation/consultation-data';
import { recomputeInvoiceItemPaymentStatus } from './invoice-item-settlement';

/** The statement surface of a PowerSync transaction (`tx`). */
export interface SqlExecutor {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

/** Only `writeTransaction` is needed from the database. */
export interface WriteTransactionDb {
  writeTransaction: <T>(callback: (tx: SqlExecutor) => Promise<T>) => Promise<T>;
}

export interface LabRequisitionResult {
  /** False when this attempt had already been written (a converging retry). */
  created: boolean;
  orderId: string;
  orderNumber: string | null;
  /** The dedicated invoice, or null when billing was unavailable (D6). */
  invoiceId: string | null;
  invoiceNo: string | null;
}

/**
 * Create a requisition atomically. The plan is built (and validated) BEFORE the
 * transaction, so a bad selection fails without taking the write lock.
 */
export async function createLabRequisition(params: {
  db: WriteTransactionDb;
  input: LabRequisitionPlanInput;
}): Promise<LabRequisitionResult> {
  const plan = buildLabRequisitionPlan(params.input);
  return params.db.writeTransaction((tx) => writeLabRequisitionPlan(tx, plan));
}

type Row = Record<string, string | number | null>;

/** The only tables a requisition plan writes. */
type PlanTable = 'lab_orders' | 'lab_order_tests' | 'invoices' | 'invoice_items';

async function insertRow(tx: SqlExecutor, table: PlanTable, row: Row): Promise<void> {
  const cols = Object.keys(row);
  await tx.execute(
    `INSERT INTO ${table} (${cols.join(', ')}) VALUES (${cols.map(() => '?').join(', ')})`,
    cols.map((c) => row[c]),
  );
}

/**
 * Next human-readable number (`LAB-00042`, `INV-00107`) — the same max+1 rule
 * `createLabOrder` / `billLabTests` use, but read through the transaction so it
 * is computed under the write lock. (Two OFFLINE devices can still mint the
 * same display number; that pre-existing limitation is unchanged.)
 */
async function nextSequentialNumber(
  tx: SqlExecutor,
  input: {
    table: 'lab_orders' | 'invoices';
    column: 'order_number' | 'invoice_no';
    prefix: string;
    orgId: string;
  },
): Promise<string> {
  const rows = await tx.getAll<Record<string, string | null>>(
    `SELECT ${input.column} AS value FROM ${input.table} WHERE org_id = ?`,
    [input.orgId],
  );
  const max = rows.reduce((m, r) => {
    const n = parseInt((r.value ?? '').replace(input.prefix, ''), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return `${input.prefix}${String(max + 1).padStart(5, '0')}`;
}

/** Map a persisted invoice_items row into the shape `invoiceSummary` expects. */
function toSummaryItem(r: {
  id: string;
  service_name: string | null;
  qty: number | string | null;
  unit_price: number | string | null;
  discount: number | string | null;
  discount_type: string | null;
  tax_rate: number | string | null;
}): InvoiceItem {
  return {
    id: r.id,
    serviceName: r.service_name ?? '',
    qty: Number(r.qty ?? 1),
    unitPrice: Number(r.unit_price ?? 0),
    discount: Number(r.discount ?? 0),
    discountType: (r.discount_type as DiscountType) ?? 'fixed',
    taxRate: Number(r.tax_rate ?? 0),
  };
}

/**
 * Execute a requisition plan through a transaction executor ONLY.
 *
 * Idempotency gate: the order id IS the attempt's `requisitionId`. Because the
 * whole plan commits (or rolls back) as one unit, an existing order row means
 * this attempt already committed in full — return it instead of writing again.
 */
export async function writeLabRequisitionPlan(
  tx: SqlExecutor,
  plan: LabRequisitionPlan,
): Promise<LabRequisitionResult> {
  const orgId = String(plan.order.org_id);

  const existing = await tx.getAll<{ id: string; order_number: string | null }>(
    `SELECT id, order_number FROM lab_orders WHERE id = ? LIMIT 1`,
    [plan.orderId],
  );
  if (existing.length > 0) {
    const inv = plan.invoiceId
      ? await tx.getAll<{ id: string; invoice_no: string | null }>(
          `SELECT id, invoice_no FROM invoices WHERE id = ? LIMIT 1`,
          [plan.invoiceId],
        )
      : [];
    return {
      created: false,
      orderId: plan.orderId,
      orderNumber: existing[0].order_number ?? null,
      invoiceId: inv[0]?.id ?? null,
      invoiceNo: inv[0]?.invoice_no ?? null,
    };
  }

  const orderNumber = await nextSequentialNumber(tx, {
    table: 'lab_orders',
    column: 'order_number',
    prefix: 'LAB-',
    orgId,
  });
  await insertRow(tx, 'lab_orders', { ...plan.order, order_number: orderNumber });
  for (const test of plan.tests) await insertRow(tx, 'lab_order_tests', test);

  if (!plan.invoice || !plan.invoiceId) {
    return { created: true, orderId: plan.orderId, orderNumber, invoiceId: null, invoiceNo: null };
  }

  const invoiceNo = await nextSequentialNumber(tx, {
    table: 'invoices',
    column: 'invoice_no',
    prefix: 'INV-',
    orgId,
  });
  await insertRow(tx, 'invoices', {
    ...plan.invoice,
    invoice_no: invoiceNo,
    subtotal: 0,
    discount_total: 0,
    tax_total: 0,
    grand_total: 0,
  });
  for (const item of plan.invoiceItems) await insertRow(tx, 'invoice_items', item);

  // Totals through the SHARED invoice summary (no parallel laboratory total),
  // read back inside the transaction.
  const itemRows = await tx.getAll<Parameters<typeof toSummaryItem>[0]>(
    `SELECT id, service_name, qty, unit_price, discount, discount_type, tax_rate
       FROM invoice_items WHERE invoice_id = ?`,
    [plan.invoiceId],
  );
  const sum = invoiceSummary(itemRows.map(toSummaryItem));
  const now = String(plan.invoice.updated_at);
  await tx.execute(
    `UPDATE invoices
        SET subtotal = ?, discount_total = ?, tax_total = ?, grand_total = ?,
            status = CASE WHEN ? > 0 THEN 'issued' ELSE status END, updated_at = ?
      WHERE id = ?`,
    [
      sum.subtotal,
      sum.discountTotal,
      sum.taxTotal,
      sum.grandTotal,
      sum.grandTotal,
      now,
      plan.invoiceId,
    ],
  );
  // Settlement pass shared with Billing — given the TRANSACTION executor.
  await recomputeInvoiceItemPaymentStatus(tx, plan.invoiceId);

  return {
    created: true,
    orderId: plan.orderId,
    orderNumber,
    invoiceId: plan.invoiceId,
    invoiceNo,
  };
}
